import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography } from '@mui/material';
import { useSearchMindRightPartnersQuery } from '../../../core/queries/useMindRightPartnersQuery';
import { MindRightPartner } from '../../../core/api/partner.api';
import { useDebounce } from '../../../core/hooks/useDebounce';

/** Format full name as "FirstName L." (e.g. "Lenita Quelino" → "Lenita Q.") */
function formatPartnerDisplayName(fullName: string): string {
  const trimmed = fullName?.trim() || '';
  if (!trimmed) return '';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0];
  const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
  return `${parts[0]} ${lastInitial}.`;
}

interface PartnerAutocompleteProps {
  value: string | null; // preferredPartnerId
  onChange: (partnerId: string | null, partnerName: string | null) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

const PartnerAutocomplete: React.FC<PartnerAutocompleteProps> = ({
  value,
  onChange,
  disabled = false,
  error = false,
  helperText,
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [selectedPartner, setSelectedPartner] = useState<MindRightPartner | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Debounce search query to avoid excessive API calls
  const debouncedSearchQuery = useDebounce(inputValue, 300);

  // Fetch partners based on search query
  const {
    data: partnersData,
    isLoading,
    isError,
  } = useSearchMindRightPartnersQuery(debouncedSearchQuery, 10);

  const partners = useMemo(() => partnersData?.partners || [], [partnersData?.partners]);

  // Load selected partner name when value changes (e.g., from form reset)
  useEffect(() => {
    if (value && !selectedPartner && partners.length > 0) {
      const partner = partners.find((p) => p._id === value);
      if (partner) {
        setSelectedPartner(partner);
        setInputValue(formatPartnerDisplayName(partner.name));
      }
    }
  }, [value, partners, selectedPartner]);

  const handleInputChange = useCallback((_event: React.SyntheticEvent, newInputValue: string, reason?: string) => {
    setInputValue(newInputValue);
    // If user clears input, clear selection
    if (!newInputValue) {
      setSelectedPartner(null);
      onChange(null, null);
    } else if (reason === 'input' && !selectedPartner) {
      // User is typing but hasn't selected from dropdown - treat as free text (unverified)
      // Only set the name, not the ID (preferredPartnerId will be null)
      // This allows free-text entry that won't trigger priority matching
      onChange(null, newInputValue);
    }
  }, [onChange, selectedPartner]);

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: MindRightPartner | null) => {
      setSelectedPartner(newValue);
      if (newValue) {
        setInputValue(formatPartnerDisplayName(newValue.name));
        onChange(newValue._id, newValue.name);
      } else {
        setInputValue('');
        onChange(null, null);
      }
    },
    [onChange]
  );

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Box>
      <Autocomplete
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        options={partners}
        getOptionLabel={(option) => (typeof option === 'string' ? option : formatPartnerDisplayName(option.name))}
        loading={isLoading}
        value={selectedPartner}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={(event, newInputValue, reason) => handleInputChange(event, newInputValue, reason)}
        disabled={disabled}
        filterOptions={(x) => x} // Disable client-side filtering since we're using server-side search
        noOptionsText={
          isError
            ? 'Error loading partners. Please try again.'
            : inputValue.length >= 1 && !isLoading
              ? 'No results found — your preferred partner may not be available.'
              : 'Start typing to search for a partner'
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="If you have a preferred MindRight Partner, please type their name below."
            variant="outlined"
            error={error}
            helperText={helperText}
            slotProps={{
              inputLabel: {
                shrink: true,
                style: {
                  whiteSpace: 'normal',
                  maxWidth: '100%',
                  lineHeight: 1.2,
                },
              },
            }}
            sx={{
              '.MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
              },
              '.MuiFormLabel-root': {
                overflow: 'visible',
                lineHeight: 1.2,
                paddingTop: { xs: '4px', sm: 0 },
              },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option._id}>
            <Typography>{formatPartnerDisplayName(option.name)}</Typography>
          </Box>
        )}
        isOptionEqualToValue={(option, value) => option._id === value._id}
      />
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 1, mb: 2, fontSize: '0.875rem' }}
      >
        Note: We will try our best to match you with your preferred MindRight Partner. If they are
        not available, we will match you with a different partner who can equally provide
        support.
      </Typography>
    </Box>
  );
};

export default PartnerAutocomplete;
